"use client";

import { ArrowUpRight, FileText, Scale, Shield } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StudioCapabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const pillars = [{
    reference: "§ 1.1 / ANIMATION",
    title: "Cinematic Storytelling",
    description: "Harness professional-grade animation tools to bring your unique vision to life. Our platform simplifies complex workflows, allowing you to craft frame-by-frame masterpieces with ease.",
    subServices: ["Character Rigging", "Motion Sequencing", "Render Optimization"],
    Icon: FileText
  }, {
    reference: "§ 1.2 / PRODUCTION",
    title: "Studio Infrastructure",
    description: "Build your own digital studio environment. We provide the backbone for high-fidelity film production, ensuring your creative assets are managed with industry-standard precision.",
    subServices: ["Asset Management", "Scene Composition", "Lighting & Texture"],
    Icon: Shield
  }, {
    reference: "§ 1.3 / DISTRIBUTION",
    title: "Global Reach",
    description: "From the storyboard to the screen, we facilitate the distribution of your films. Reach audiences worldwide with our integrated publishing and streaming capabilities.",
    subServices: ["Format Exporting", "Platform Integration", "Audience Analytics"],
    Icon: Scale
  }];
  return <section data-section-id="2803" id="studio-capabilities" className="bg-background text-foreground py-24 md:py-36 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-muted-foreground mb-8">
            § 01 — STUDIO CAPABILITIES
          </p>
          <h2 className="font-default font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl mx-auto">
            The future
            <br />
            of{" "}
            <em className="font-default italic font-normal" style={{
            color: "var(--primary)"
          }}>
              animation
            </em>
            .
          </h2>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 relative transition-all duration-1000 delay-200 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {pillars.map((pillar, i) => {
          const Icon = pillar.Icon;
          return <div data-index={i} key={pillar.reference} className={`group relative px-6 md:px-10 py-12 md:py-8 transition-all duration-500 ${i > 0 ? "md:border-l border-t md:border-t-0 border-border group-hover:border-primary" : ""}`}>
                {}
                <div className="hidden md:block absolute top-0 bottom-0 left-0 w-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              background: "linear-gradient(to bottom, transparent 0%, var(--primary) 50%, transparent 100%)",
              boxShadow: "0 0 12px var(--primary)"
            }} aria-hidden="true" />
                <div className="hidden md:block absolute top-0 bottom-0 right-0 w-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              background: "linear-gradient(to bottom, transparent 0%, var(--primary) 50%, transparent 100%)",
              boxShadow: "0 0 12px var(--primary)"
            }} aria-hidden="true" />

                {}
                <div className="mb-10 flex items-start">
                  <Icon className="w-24 h-24 transition-colors duration-500" strokeWidth={1.25} style={{
                color: "var(--primary)"
              }} aria-hidden="true" />
                </div>

                {}
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-5">
                  {pillar.reference}
                </p>

                {}
                <h3 className="font-default font-normal text-2xl md:text-3xl leading-tight mb-6 text-foreground">
                  {pillar.title}
                </h3>

                {}
                <p className="font-default text-sm md:text-[15px] leading-relaxed text-muted-foreground mb-8">
                  {pillar.description}
                </p>

                {}
                <ul className="space-y-3 mb-10">
                  {pillar.subServices.map((service, j) => <li data-index={j} key={service} className="flex items-center gap-3 font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/80">
                      <span className="w-1 h-1 rounded-lg shrink-0" style={{
                  backgroundColor: "var(--primary)"
                }} aria-hidden="true" />
                      <span>{service}</span>
                    </li>)}
                </ul>

                {}
                <Link to="/AboutPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-foreground/70 hover:text-foreground transition-colors duration-300 group/link">
                  
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" strokeWidth={1.5} />
                </Link>
              </div>;
        })}
        </div>
      </div>
    </section>;
}